/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  BarChart, Bar, ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell,
} from 'recharts';
import { LayoutDashboard, TrendingUp, CalendarCheck, PiggyBank, Users, RefreshCw, Euro, Settings2, Stethoscope } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { getDirezioneOverview, getDirezioneSettings, putDirezioneSettings } from '@/lib/enterprise';

/**
 * DirezionePage — Dashboard direzionale (ROI & statistiche) per la direzione
 * della clinica: fatturato, saturazione agende, risparmio SaaS cumulativo
 * (contatore dinamico che cresce in tempo reale).
 */

const euro = (n, d = 2) => `€ ${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: d, maximumFractionDigits: d })}`;

// Recharts style constants (hoisted: avoids re-creating inline objects on every render)
const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };
const TOOLTIP_STYLE = { borderRadius: 12, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--foreground))', fontSize: 12 };
const LEGEND_STYLE = { fontSize: 12 };
const REVENUE_MARGIN = { top: 8, right: 8, left: -12, bottom: 0 };
const SATURATION_MARGIN = { top: 0, right: 8, left: 8, bottom: 0 };
const BAR_RADIUS_TOP = [6, 6, 0, 0];
const BAR_RADIUS_RIGHT = [0, 6, 6, 0];
const LINE_DOT = { r: 3 };
const PCT_DOMAIN = [0, 100];
const seriesLabel = (n) => (n === 'invoiced' ? 'Fatturato' : 'Visite online');
const revenueTooltipFormatter = (v, n) => [euro(v), seriesLabel(n)];
const saturationTooltipFormatter = (v) => [`${v}%`, 'Saturazione'];

const Kpi = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <Card className="rounded-xl" data-testid={testId}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0', accent ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary')}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
        <div className="font-display font-bold text-xl leading-tight truncate">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground truncate">{sub}</div>}
      </div>
    </CardContent>
  </Card>
);

/** Contatore che cresce ogni secondo in base a per_second_eur. */
const SavingsCounter = ({ savings }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);
  const value = useMemo(() => {
    if (!savings) return 0;
    const base = Number(savings.cumulative_eur || 0);
    const elapsed = (now - new Date(savings._loaded_at || now).getTime()) / 1000;
    return base + elapsed * Number(savings.per_second_eur || 0);
  }, [savings, now]);
  return (
    <div className="font-display font-bold text-4xl md:text-5xl tabular-nums text-primary" data-testid="saas-savings-counter">
      {euro(value, 2)}
    </div>
  );
};

export default function DirezionePage() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(12);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [fee, setFee] = useState('49');
  const [savingFee, setSavingFee] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, st] = await Promise.all([getDirezioneOverview(months), getDirezioneSettings().catch(() => null)]);
      setData({ ...ov, saas_savings: { ...ov.saas_savings, _loaded_at: new Date().toISOString() } });
      if (st) { setSettings(st); setFee(String(st.saas_fee_per_user_eur)); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile caricare la dashboard');
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => { load(); }, [load]);

  const saveFee = async () => {
    setSavingFee(true);
    try {
      await putDirezioneSettings({ ...(settings || {}), saas_fee_per_user_eur: Number(fee) || 0 });
      toast.success('Tariffa SaaS di riferimento aggiornata');
      load();
    } catch (e) { toast.error('Salvataggio non riuscito'); } finally { setSavingFee(false); }
  };

  const k = data?.kpis || {};
  const s = data?.saas_savings;

  return (
    <div className="space-y-5" data-testid="direzione-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2"><LayoutDashboard className="h-6 w-6 text-primary" /> Dashboard direzionale</h1>
          <p className="text-sm text-muted-foreground mt-1">Fatturato, saturazione agende e ROI della piattaforma white-label.</p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <TabsList className="h-9 rounded-lg">
              {[6, 12, 24].map((m) => <TabsTrigger key={m} value={String(m)} className="text-xs rounded-md" data-testid={`direzione-months-${m}`}>{m} mesi</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <Button variant="outline" size="icon" onClick={load} aria-label="Aggiorna" data-testid="direzione-refresh"><RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} /></Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
          <Kpi icon={Euro} label="Fatturato periodo" value={euro(k.invoiced_total, 0)} sub={`+ ${euro(k.visits_total, 0)} visite online`} testId="kpi-invoiced" />
          <Kpi icon={CalendarCheck} label="Saturazione agende" value={`${data?.saturation_avg_pct ?? 0}%`} sub={`${k.appointments_30d ?? 0} appuntamenti / 30 gg`} accent testId="kpi-saturation" />
          <Kpi icon={Users} label="Utenti attivi" value={k.active_professionals ?? 0} sub={`${k.patients ?? 0} pazienti`} testId="kpi-users" />
          <Kpi icon={Stethoscope} label="No-show 30 gg" value={`${k.no_show_rate_pct ?? 0}%`} sub="tasso mancate presentazioni" accent testId="kpi-noshow" />
        </div>
      )}

      {/* Risparmio SaaS */}
      <Card className="rounded-xl border-primary/30 bg-primary/5" data-testid="saas-savings-card">
        <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-5">
          <div className="h-14 w-14 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0"><PiggyBank className="h-7 w-7" /></div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Risparmio SaaS cumulativo</div>
            {s ? <SavingsCounter savings={s} /> : <Skeleton className="h-12 w-64" />}
            {s && (
              <p className="text-xs text-muted-foreground mt-1">
                {s.active_users} utenti attivi × {euro(s.monthly_fee_per_user, 0)}/mese evitati = <b>{euro(s.monthly_eur, 0)}/mese</b> · dal {new Date(s.since).toLocaleDateString('it-IT')} ({s.months_active} mesi)
              </p>
            )}
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="saas-fee-settings"><Settings2 className="h-4 w-4" /> Tariffa di riferimento</Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 rounded-xl glass-strong">
              <Label className="text-xs">Canone SaaS evitato (€/utente/mese)</Label>
              <div className="flex gap-2 mt-1">
                <Input type="number" min={0} step="1" value={fee} onChange={(e) => setFee(e.target.value)} className="h-9" data-testid="saas-fee-input" />
                <Button size="sm" onClick={saveFee} disabled={savingFee} data-testid="saas-fee-save">Salva</Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Canone medio di un gestionale cloud per posto/mese. Il contatore = utenti attivi × tariffa × mesi di utilizzo.</p>
            </PopoverContent>
          </Popover>
        </CardContent>
      </Card>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {/* Fatturato */}
        <Card className="rounded-xl" data-testid="revenue-trend-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-display flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Andamento fatturato</CardTitle>
            <CardDescription>Fatture emesse (barre) e visite pagate online (linea), per mese.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading && !data ? <Skeleton className="h-64 rounded-xl" /> : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data?.revenue || []} margin={REVENUE_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <Tooltip formatter={revenueTooltipFormatter} contentStyle={TOOLTIP_STYLE} />
                    <Legend formatter={seriesLabel} wrapperStyle={LEGEND_STYLE} />
                    <Bar dataKey="invoiced" fill="hsl(var(--primary))" radius={BAR_RADIUS_TOP} maxBarSize={36} />
                    <Line type="monotone" dataKey="visits_paid" stroke="hsl(var(--accent))" strokeWidth={2.5} dot={LINE_DOT} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Saturazione */}
        <Card className="rounded-xl" data-testid="saturation-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-display flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-primary" /> Saturazione agende</CardTitle>
            <CardDescription>Slot prenotati / capacità disponibile negli ultimi 30 giorni.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading && !data ? <Skeleton className="h-64 rounded-xl" /> : (data?.saturation || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessun medico con disponibilità configurata.</p>
            ) : (
              <>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.saturation.slice(0, 6)} layout="vertical" margin={SATURATION_MARGIN}>
                      <XAxis type="number" domain={PCT_DOMAIN} hide />
                      <YAxis type="category" dataKey="doctor_name" width={110} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                      <Tooltip formatter={saturationTooltipFormatter} contentStyle={TOOLTIP_STYLE} />
                      <Bar dataKey="saturation_pct" radius={BAR_RADIUS_RIGHT} maxBarSize={18}>
                        {data.saturation.slice(0, 6).map((d) => (
                          <Cell key={d.doctor_id} fill={d.saturation_pct > 85 ? 'hsl(var(--warning))' : 'hsl(var(--primary))'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <ul className="space-y-2">
                  {data.saturation.slice(0, 5).map((d) => (
                    <li key={d.doctor_id} className="text-xs" data-testid={`saturation-row-${d.doctor_id}`}>
                      <div className="flex items-center justify-between mb-1"><span className="font-medium truncate">{d.doctor_name}</span><Badge variant="outline" className="text-[10px]">{d.booked_slots}/{d.capacity_slots} slot</Badge></div>
                      <Progress value={d.saturation_pct} className="h-1.5" />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
